import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import { 
  Zap, TrendingUp, TrendingDown, Play, Loader2, RefreshCw,
  Search, Calendar, Building2, DollarSign, Clock
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const BuySellPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [buyEvents, setBuyEvents] = useState([]);
  const [sellEvents, setSellEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Filters
  const [yearFilter, setYearFilter] = useState("");
  const [quarterFilter, setQuarterFilter] = useState("");
  const [symbolFilter, setSymbolFilter] = useState("");
  const [quartersToRecompute, setQuartersToRecompute] = useState(4);
  
  // Historical processing
  const [startYear, setStartYear] = useState(2020);
  const [endYear, setEndYear] = useState(new Date().getFullYear());
  const [processingHistorical, setProcessingHistorical] = useState(false);
  
  // Available quarters
  const [availableQuarters, setAvailableQuarters] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchAvailableQuarters();
    // Fetch both buy and sell events on load for Overview tab
    fetchBuyEvents();
    fetchSellEvents();
  }, [token]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/buy-sell/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableQuarters = async () => {
    try {
      const response = await fetch(`${API}/buy-sell/available-quarters`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableQuarters(data.quarters || []);
      }
    } catch (error) {
      console.error("Failed to fetch quarters:", error);
    }
  };

  const fetchBuyEvents = async () => {
    try {
      let url = `${API}/buy-sell/buy-events?limit=100`;
      if (yearFilter) url += `&year=${yearFilter}`;
      if (quarterFilter) url += `&quarter=${quarterFilter}`;
      if (symbolFilter) url += `&cusip_symbol=${symbolFilter.toUpperCase()}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setBuyEvents(data.events || []);
      }
    } catch (error) {
      console.error("Failed to fetch buy events:", error);
    }
  };

  const fetchSellEvents = async () => {
    try {
      let url = `${API}/buy-sell/sell-events?limit=100`;
      if (yearFilter) url += `&year=${yearFilter}`;
      if (quarterFilter) url += `&quarter=${quarterFilter}`;
      if (symbolFilter) url += `&cusip_symbol=${symbolFilter.toUpperCase()}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSellEvents(data.events || []);
      }
    } catch (error) {
      console.error("Failed to fetch sell events:", error);
    }
  };

  useEffect(() => {
    if (activeTab === "buys") {
      fetchBuyEvents();
    } else if (activeTab === "sells") {
      fetchSellEvents();
    }
  }, [activeTab, yearFilter, quarterFilter, symbolFilter]);

  const runBuySellPipeline = async () => {
    setRunning(true);
    try {
      const response = await fetch(
        `${API}/buy-sell/run?recompute_last_n_quarters=${quartersToRecompute}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        toast.success(
          `Generated ${result.buy_events_created} buy events and ${result.sell_events_created} sell events`
        );
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to run buy/sell pipeline");
      }
    } catch (error) {
      toast.error("Failed to run buy/sell pipeline");
    } finally {
      setRunning(false);
    }
  };

  const runForQuarter = async (year, quarter) => {
    setRunning(true);
    try {
      const response = await fetch(
        `${API}/buy-sell/run-quarter?year=${year}&quarter=${quarter}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        toast.success(
          `${year}Q${quarter}: ${result.buy_events_created} buys, ${result.sell_events_created} sells`
        );
        fetchStats();
        fetchAvailableQuarters();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to run for quarter");
      }
    } catch (error) {
      toast.error("Failed to run for quarter");
    } finally {
      setRunning(false);
    }
  };

  const reprocessQuarter = async (year, quarter) => {
    if (!confirm(`Reprocess ${year} Q${quarter}? This will delete existing events and regenerate them.`)) {
      return;
    }
    
    setRunning(true);
    try {
      const response = await fetch(
        `${API}/buy-sell/reprocess-quarter?year=${year}&quarter=${quarter}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        toast.success(
          `${year}Q${quarter}: Deleted ${result.deleted?.buy_events_deleted || 0} old, created ${result.created?.buy_events || 0} new buys`
        );
        fetchStats();
        fetchAvailableQuarters();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to reprocess quarter");
      }
    } catch (error) {
      toast.error("Failed to reprocess quarter");
    } finally {
      setRunning(false);
    }
  };

  const runFromYear = async () => {
    setProcessingHistorical(true);
    try {
      const response = await fetch(
        `${API}/buy-sell/run-from-year?start_year=${startYear}&end_year=${endYear}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          toast.success(
            `Processed ${result.quarters_processed} quarters: ${result.total_buy_events} buys, ${result.total_sell_events} sells`
          );
        } else {
          toast.warning(result.message || "No data processed");
        }
        fetchStats();
        fetchAvailableQuarters();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to process historical data");
      }
    } catch (error) {
      toast.error("Failed to process historical data");
    } finally {
      setProcessingHistorical(false);
    }
  };

  const formatValue = (value) => {
    if (!value && value !== 0) return "$0";
    const absValue = Math.abs(value);
    const sign = value < 0 ? "-" : "";
    if (absValue >= 1e9) return `${sign}$${(absValue / 1e9).toFixed(2)}B`;
    if (absValue >= 1e6) return `${sign}$${(absValue / 1e6).toFixed(2)}M`;
    if (absValue >= 1e3) return `${sign}$${(absValue / 1e3).toFixed(2)}K`;
    return `${sign}$${absValue.toLocaleString('en-US')}`;
  };

  const formatNumber = (value) => {
    if (!value && value !== 0) return "0";
    return value.toLocaleString('en-US');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Buy/Sell Events</h1>
          <p className="text-sm text-muted-foreground">
            Fund position changes detected from 13F holdings
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={quartersToRecompute}
            onChange={(e) => setQuartersToRecompute(parseInt(e.target.value) || 4)}
            className="w-20 bg-background border-border rounded-sm"
            min={1}
            max={20}
          />
          <span className="text-xs text-muted-foreground">quarters</span>
          <Button
            onClick={runBuySellPipeline}
            disabled={running}
            className="rounded-sm"
            data-testid="run-buysell-btn"
          >
            {running ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Generate Events
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Buy Events</p>
                <p className="text-2xl font-bold text-green-400">
                  {stats?.total_buy_events?.toLocaleString() || 0}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-400/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Sell Events</p>
                <p className="text-2xl font-bold text-red-400">
                  {stats?.total_sell_events?.toLocaleString() || 0}
                </p>
              </div>
              <TrendingDown className="w-8 h-8 text-red-400/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">New Positions</p>
                <p className="text-2xl font-bold">{stats?.buy_new?.toLocaleString() || 0}</p>
              </div>
              <Zap className="w-8 h-8 text-primary/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Full Exits</p>
                <p className="text-2xl font-bold">{stats?.sell_exit?.toLocaleString() || 0}</p>
              </div>
              <TrendingDown className="w-8 h-8 text-muted-foreground/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline State */}
      {stats?.state && (
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Pipeline State</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Last run:</span>
                <span>
                  {stats.state.last_run_at
                    ? new Date(stats.state.last_run_at).toLocaleString()
                    : "Never"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={stats.state.last_run_status === "completed" ? "default" : "secondary"}>
                  {stats.state.last_run_status || "idle"}
                </Badge>
              </div>
              {stats.state.last_processed_year && (
                <div>
                  <span className="text-muted-foreground">Last processed: </span>
                  <span>{stats.state.last_processed_year}Q{stats.state.last_processed_quarter}</span>
                </div>
              )}
              {stats.state.error_count > 0 && (
                <Badge variant="destructive">{stats.state.error_count} errors</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted rounded-sm">
          <TabsTrigger value="overview" className="rounded-sm">Overview</TabsTrigger>
          <TabsTrigger value="buys" className="rounded-sm">
            <TrendingUp className="w-4 h-4 mr-1 text-green-400" />
            Buy Events
          </TabsTrigger>
          <TabsTrigger value="sells" className="rounded-sm">
            <TrendingDown className="w-4 h-4 mr-1 text-red-400" />
            Sell Events
          </TabsTrigger>
          <TabsTrigger value="quarters" className="rounded-sm">
            <Calendar className="w-4 h-4 mr-1" />
            By Quarter
          </TabsTrigger>
          <TabsTrigger value="historical" className="rounded-sm">
            <Clock className="w-4 h-4 mr-1" />
            Historical
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-card border-border rounded-sm">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  Recent Buy Events
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  {buyEvents.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No buy events yet. Run the pipeline to generate events.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {buyEvents.slice(0, 10).map((event, idx) => (
                        <div key={idx} className="p-2 bg-muted/50 rounded-sm">
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-sm">{event.cusip_symbol}</span>
                            <Badge variant="outline" className="text-green-400 border-green-400/30">
                              {event.event_type}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between mt-1 text-xs text-muted-foreground">
                            <span>{event.entity_name?.substring(0, 30)}...</span>
                            <span>{event.year}Q{event.quarter}</span>
                          </div>
                          <div className="text-xs text-green-400 mt-1">
                            +{formatValue(event.current_value)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            <Card className="bg-card border-border rounded-sm">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-red-400" />
                  Recent Sell Events
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  {sellEvents.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No sell events yet. Run the pipeline to generate events.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {sellEvents.slice(0, 10).map((event, idx) => (
                        <div key={idx} className="p-2 bg-muted/50 rounded-sm">
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-sm">{event.cusip_symbol}</span>
                            <Badge variant="outline" className="text-red-400 border-red-400/30">
                              {event.event_type}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between mt-1 text-xs text-muted-foreground">
                            <span>{event.entity_name?.substring(0, 30)}...</span>
                            <span>{event.year}Q{event.quarter}</span>
                          </div>
                          <div className="text-xs text-red-400 mt-1">
                            -{formatValue(event.previous_value)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Buy Events Tab */}
        <TabsContent value="buys" className="mt-4">
          <Card className="bg-card border-border rounded-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Buy Events</CardTitle>
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Symbol"
                    value={symbolFilter}
                    onChange={(e) => setSymbolFilter(e.target.value)}
                    className="w-24 bg-background rounded-sm text-xs"
                  />
                  <Input
                    placeholder="Year"
                    type="number"
                    value={yearFilter}
                    onChange={(e) => setYearFilter(e.target.value)}
                    className="w-20 bg-background rounded-sm text-xs"
                  />
                  <Input
                    placeholder="Q"
                    type="number"
                    value={quarterFilter}
                    onChange={(e) => setQuarterFilter(e.target.value)}
                    className="w-16 bg-background rounded-sm text-xs"
                    min={1}
                    max={4}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchBuyEvents}
                    className="rounded-sm"
                  >
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left py-2">Symbol</th>
                      <th className="text-left py-2">Fund</th>
                      <th className="text-left py-2">Quarter</th>
                      <th className="text-right py-2">Value</th>
                      <th className="text-left py-2">Sector</th>
                      <th className="text-left py-2">Obs. Date</th>
                      <th className="text-left py-2">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buyEvents.map((event, idx) => (
                      <tr key={idx} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-2 font-mono">{event.cusip_symbol}</td>
                        <td className="py-2 text-xs max-w-[200px] truncate">
                          {event.entity_name}
                        </td>
                        <td className="py-2">{event.year}Q{event.quarter}</td>
                        <td className="py-2 text-right text-green-400">
                          {formatValue(event.current_value)}
                        </td>
                        <td className="py-2 text-xs text-muted-foreground">
                          {event.company_sector || "-"}
                        </td>
                        <td className="py-2 text-xs text-muted-foreground">
                          {event.observation_date ? new Date(event.observation_date).toLocaleDateString() : "-"}
                        </td>
                        <td className="py-2">
                          <Badge variant="outline" className="text-xs">
                            {event.event_type}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sell Events Tab */}
        <TabsContent value="sells" className="mt-4">
          <Card className="bg-card border-border rounded-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Sell Events</CardTitle>
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Symbol"
                    value={symbolFilter}
                    onChange={(e) => setSymbolFilter(e.target.value)}
                    className="w-24 bg-background rounded-sm text-xs"
                  />
                  <Input
                    placeholder="Year"
                    type="number"
                    value={yearFilter}
                    onChange={(e) => setYearFilter(e.target.value)}
                    className="w-20 bg-background rounded-sm text-xs"
                  />
                  <Input
                    placeholder="Q"
                    type="number"
                    value={quarterFilter}
                    onChange={(e) => setQuarterFilter(e.target.value)}
                    className="w-16 bg-background rounded-sm text-xs"
                    min={1}
                    max={4}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchSellEvents}
                    className="rounded-sm"
                  >
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left py-2">Symbol</th>
                      <th className="text-left py-2">Fund</th>
                      <th className="text-left py-2">Quarter</th>
                      <th className="text-right py-2">Prev Value</th>
                      <th className="text-left py-2">Sector</th>
                      <th className="text-left py-2">Obs. Date</th>
                      <th className="text-left py-2">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sellEvents.map((event, idx) => (
                      <tr key={idx} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-2 font-mono">{event.cusip_symbol}</td>
                        <td className="py-2 text-xs max-w-[200px] truncate">
                          {event.entity_name}
                        </td>
                        <td className="py-2">{event.year}Q{event.quarter}</td>
                        <td className="py-2 text-right text-red-400">
                          {formatValue(event.previous_value)}
                        </td>
                        <td className="py-2 text-xs text-muted-foreground">
                          {event.company_sector || "-"}
                        </td>
                        <td className="py-2 text-xs text-muted-foreground">
                          {event.observation_date ? new Date(event.observation_date).toLocaleDateString() : "-"}
                        </td>
                        <td className="py-2">
                          <Badge variant="outline" className="text-xs">
                            {event.event_type}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* By Quarter Tab */}
        <TabsContent value="quarters" className="mt-4">
          <Card className="bg-card border-border rounded-sm">
            <CardHeader>
              <CardTitle className="text-sm">Run by Quarter</CardTitle>
              <CardDescription>
                Generate buy/sell events for specific quarters
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {availableQuarters.slice(-12).reverse().map((q, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    onClick={() => runForQuarter(q.year, q.quarter)}
                    disabled={running}
                    className="rounded-sm"
                  >
                    {running ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3 h-3 mr-1" />
                    )}
                    {q.year}Q{q.quarter}
                  </Button>
                ))}
              </div>
              {availableQuarters.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No quarters with holdings data available. Run the main pipeline first.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Historical Processing Tab */}
        <TabsContent value="historical" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Process Historical Data Card */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Process Historical Buy/Sell Events
                </CardTitle>
                <CardDescription>
                  Generate buy/sell events from historical holdings data.
                  Required for Fund Intelligence to have enough historical events.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm">
                  <p className="font-semibold mb-2">Why process historical data?</p>
                  <p className="text-muted-foreground">
                    The Fund Intelligence Engine needs buy events going back 2-3 years to compute 
                    meaningful skill estimates. If you only have recent buy events, the engine can't 
                    observe 12-month forward outcomes.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Start Year</Label>
                    <Input
                      type="number"
                      min={2015}
                      max={new Date().getFullYear()}
                      value={startYear}
                      onChange={(e) => setStartYear(parseInt(e.target.value) || 2020)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">End Year</Label>
                    <Input
                      type="number"
                      min={2015}
                      max={new Date().getFullYear()}
                      value={endYear}
                      onChange={(e) => setEndYear(parseInt(e.target.value) || new Date().getFullYear())}
                      className="mt-1"
                    />
                  </div>
                </div>

                <Button 
                  onClick={runFromYear}
                  disabled={processingHistorical || running}
                  className="w-full"
                  data-testid="run-from-year-btn"
                >
                  {processingHistorical ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing {startYear}-{endYear}...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Process Buy/Sell Events ({startYear} - {endYear})
                    </>
                  )}
                </Button>

                <p className="text-xs text-muted-foreground">
                  This will process all available quarters from holdings_enriched within the selected year range.
                </p>
              </CardContent>
            </Card>

            {/* Data Coverage Summary */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Data Coverage
                </CardTitle>
                <CardDescription>
                  Quarters with holdings data and buy/sell events
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total Quarters Available:</span>
                    <span className="font-semibold">{availableQuarters.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Quarters with Buy Events:</span>
                    <span className="font-semibold text-green-400">
                      {availableQuarters.filter(q => q.processed).length}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Quarters Not Processed:</span>
                    <span className="font-semibold text-yellow-400">
                      {availableQuarters.filter(q => !q.processed).length}
                    </span>
                  </div>
                </div>

                <ScrollArea className="h-[300px]">
                  <div className="space-y-1">
                    {availableQuarters.map((q) => (
                      <div 
                        key={`${q.year}-${q.quarter}`}
                        className={`flex items-center justify-between p-2 rounded text-sm ${
                          q.processed ? 'bg-green-500/10' : q.has_previous === false ? 'bg-red-500/10' : 'bg-muted/30'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-mono">{q.year} Q{q.quarter}</span>
                          {q.has_previous === false && (
                            <span className="text-xs text-red-400" title={q.not_processed_reason}>
                              (no prev quarter)
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-muted-foreground text-xs">
                            {q.holdings_count?.toLocaleString()} holdings
                          </span>
                          {q.processed ? (
                            <>
                              <Badge variant="outline" className="text-green-400 border-green-400/30">
                                {q.buy_events?.toLocaleString()} buys
                              </Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs"
                                onClick={() => reprocessQuarter(q.year, q.quarter)}
                                disabled={running}
                              >
                                <RefreshCw className="w-3 h-3 mr-1" />
                                Reprocess
                              </Button>
                            </>
                          ) : q.has_previous === false ? (
                            <Badge variant="outline" className="text-red-400 border-red-400/30">
                              No previous Q
                            </Badge>
                          ) : (
                            <>
                              <Badge variant="outline" className="text-yellow-400 border-yellow-400/30">
                                Not processed
                              </Badge>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 px-2 text-xs"
                                onClick={() => runForQuarter(q.year, q.quarter)}
                                disabled={running}
                              >
                                <Play className="w-3 h-3 mr-1" />
                                Process
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                    {availableQuarters.length === 0 && (
                      <p className="text-center text-muted-foreground py-4">
                        No holdings data available. Run 13F ingestion first.
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BuySellPage;
